import React from 'react';
import { Box, Typography, IconButton, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Edit as EditIcon,
  FindInPage as FindingIcon,
  UnfoldLess as CompressIcon,
} from '@mui/icons-material';
import CopyIconButton from '../../../shared/components/CopyIconButton';
import TapTooltip from '../../../shared/components/TapTooltip';
import MarkdownContent from '../../../shared/components/MarkdownContent';
import { BlockRenderer } from '../../../shared/components/blocks';
import { ReportBlock, TokenUsage } from '../../../types';
import { agentTurnTokens, subagentTurnTokens, totalTurnTokens } from '../utils/tokenUsage';
import { safeFormatTime } from '../../../shared';
import { useViewport } from '../../../shared/hooks/useViewport';
import { TOUCH_TARGET_SX } from '../../../shared/constants/layout';

interface MessageViewModeProps {
  content: string;
  timestamp: string;
  isUser: boolean;
  tokenUsage?: TokenUsage;
  blocks?: ReportBlock[];
  onEdit?: () => void;
  onAddAsFinding?: (content: string) => void;
}

const MessageViewMode: React.FC<MessageViewModeProps> = ({
  content,
  timestamp,
  isUser,
  tokenUsage,
  blocks,
  onEdit,
  onAddAsFinding,
}) => {
  const theme = useTheme();
  const { isTouch } = useViewport();
  const touchTargetSx = isTouch ? TOUCH_TARGET_SX : {};

  return (
    <>
      {isUser ? (
        <Typography variant="body1">{content}</Typography>
      ) : (
        <MarkdownContent content={content} syntaxHighlight />
      )}
      {blocks && blocks.length > 0 && (
        <Box sx={{ mt: content ? 1.5 : 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {blocks.map((block, index) => (
            <BlockRenderer key={index} block={block} />
          ))}
        </Box>
      )}
      {!isUser && (tokenUsage?.compacted_messages ?? 0) > 0 && (
        <Box
          sx={{
            mt: 1.5,
            pt: 1,
            borderTop: '1px dashed',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
          }}
        >
          <CompressIcon sx={{ fontSize: '0.9rem', opacity: 0.6 }} />
          <Typography variant="caption" sx={{ opacity: 0.7, fontSize: '0.75rem' }}>
            {tokenUsage!.compacted_messages} earlier messages were summarised to free context.
            The agent no longer has them word for word.
          </Typography>
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mt: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography
            variant="caption"
            sx={{
              opacity: 0.6,
              fontSize: '0.75rem',
            }}
          >
            {safeFormatTime(timestamp)}
          </Typography>
          {!isUser && tokenUsage && (
            <TapTooltip
              title={
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                  <Typography variant="caption">Input (uncached): {(tokenUsage.input_tokens ?? 0).toLocaleString()} tokens</Typography>
                  <Typography variant="caption">Output: {(tokenUsage.output_tokens ?? 0).toLocaleString()} tokens</Typography>
                  <Typography variant="caption">Cache read: {(tokenUsage.cache_read_tokens ?? 0).toLocaleString()} tokens</Typography>
                  <Typography variant="caption">Cache write: {(tokenUsage.cache_write_tokens ?? 0).toLocaleString()} tokens</Typography>
                  <Typography variant="caption">Agent total: {agentTurnTokens(tokenUsage).toLocaleString()} tokens</Typography>
                  {tokenUsage.subagent_calls ? (
                    <Typography variant="caption">
                      Sub-agents ({tokenUsage.subagent_calls}): {subagentTurnTokens(tokenUsage).toLocaleString()} tokens
                      {tokenUsage.subagent_model_id && tokenUsage.subagent_model_id !== tokenUsage.model_id
                        ? ` on ${tokenUsage.subagent_model_id}`
                        : ''}
                    </Typography>
                  ) : null}
                  <Typography variant="caption">
                    Turn total: {totalTurnTokens(tokenUsage).toLocaleString()} tokens spent
                    {(tokenUsage.cycle_count ?? 0) > 1 ? ' across every cycle' : ''}
                  </Typography>
                  <Typography variant="caption">LLM cycles: {tokenUsage.cycle_count ?? 0}</Typography>
                  {tokenUsage.compacted_messages ? (
                    <Typography variant="caption">
                      Compacted: {tokenUsage.compacted_messages} earlier messages summarised
                    </Typography>
                  ) : null}
                  {tokenUsage.context_tokens != null && (
                    <Typography variant="caption">
                      Context after turn: {tokenUsage.context_tokens.toLocaleString()}
                      {tokenUsage.max_context_tokens ? ` / ${tokenUsage.max_context_tokens.toLocaleString()}` : ''} tokens
                    </Typography>
                  )}
                  {tokenUsage.latency_ms != null && (
                    <Typography variant="caption">Model latency: {(tokenUsage.latency_ms / 1000).toFixed(1)}s</Typography>
                  )}
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>{tokenUsage.model_id}</Typography>
                </Box>
              }
              placement="top"
              arrow
            >
              <Typography
                variant="caption"
                sx={{
                  opacity: 0.6,
                  fontSize: '0.75rem',
                  cursor: 'default',
                }}
              >
                {/* What the turn consumed across every cycle, which is a different
                    quantity from the context chip's level and legitimately exceeds
                    it on a multi-cycle turn. */}
                {totalTurnTokens(tokenUsage).toLocaleString()} spent
                {tokenUsage.cycle_count != null ? ` · ${tokenUsage.cycle_count} cycles` : ''}
                {tokenUsage.subagent_calls ? ` · ${tokenUsage.subagent_calls} sub-agent` : ''}
              </Typography>
            </TapTooltip>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            gap: 0.5,
          }}
        >
          {!isUser && onAddAsFinding && (
            <IconButton
              size="small"
              onClick={() => onAddAsFinding(content)}
              aria-label="Add as finding"
              sx={{
                ...touchTargetSx,
                color: 'primary.main',
                opacity: 0.7,
                '&:hover': {
                  opacity: 1,
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                },
              }}
              title="Add as finding"
            >
              <FindingIcon fontSize="small" />
            </IconButton>
          )}
          <CopyIconButton
            text={content}
            tooltip="Copy message"
            sx={{
              ...touchTargetSx,
              color: isUser ? 'primary.contrastText' : 'primary.main',
              opacity: 0.7,
              '&:hover': {
                opacity: 1,
                bgcolor: isUser
                  ? alpha(theme.palette.common.white, 0.2)
                  : alpha(theme.palette.primary.main, 0.08),
              },
            }}
          />
          {isUser && onEdit && (
            <IconButton
              size="small"
              onClick={onEdit}
              aria-label="Edit message"
              sx={{
                ...touchTargetSx,
                color: 'primary.contrastText',
                opacity: 0.7,
                '&:hover': {
                  opacity: 1,
                  bgcolor: alpha(theme.palette.common.white, 0.2),
                },
              }}
              title="Edit message"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>
    </>
  );
};

export default MessageViewMode;
